import Phaser from 'phaser';
import type { AthleteView, Pose } from './AthleteView';

/**
 * Articulated biped rig: assembles cut Higgsfield parts (head, torso, 2 arms, 2 legs,
 * tail) on a humanoid skeleton and animates a run cycle — legs stride, arms pump in
 * counter-phase, body bobs and leans.
 *
 * Generated parts differ per animal (a limb may be drawn diagonal vs vertical), so each
 * skin can override the geometry (part pivots, rest angles, joint offsets). Geometry was
 * tuned offline with scripts/compose-test.mjs. Same shape implements AthleteView.
 */
export interface RigParts {
  head: string;
  torso: string;
  arm: string;
  leg: string;
  /** Optional — some athletes (e.g. gorilla) have no tail. */
  tail?: string;
}

export interface RigGeometry {
  /** Pivot of each part as a fraction of its image. */
  origin: { torso: [number, number]; head: [number, number]; arm: [number, number]; leg: [number, number]; tail: [number, number] };
  /** Rest rotation (rad) that makes a part hang naturally given how it was drawn. */
  rest: { arm: number; leg: number; tail: number; head: number; torso: number };
  /** Joint positions relative to the hip: [x px, y as fraction of torso height]. */
  joints: { neck: [number, number]; shoulder: [number, number]; hip: [number, number]; tail: [number, number] };
  legSwing: number;
  armSwing: number;
}

/** Default geometry — tuned for the cheetah part-set (diagonal arm). */
const DEFAULT_GEOMETRY: RigGeometry = {
  origin: { torso: [0.5, 0.9], head: [0.5, 0.92], arm: [0.2, 0.16], leg: [0.5, 0.06], tail: [0.88, 0.22] },
  rest: { arm: 1.15, leg: 0.0, tail: -0.5, head: 0.0, torso: 0.0 },
  joints: { neck: [10, -0.82], shoulder: [6, -0.66], hip: [0, -0.04], tail: [-40, -0.4] },
  legSwing: 0.95,
  armSwing: 1.05,
};

const FAR_TINT = 0xbcbcbc;

export class BipedRig implements AthleteView {
  readonly container: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Container;
  private readonly torso: Phaser.GameObjects.Sprite;
  private readonly head: Phaser.GameObjects.Sprite;
  private readonly armNear: Phaser.GameObjects.Sprite;
  private readonly armFar: Phaser.GameObjects.Sprite;
  private readonly legNear: Phaser.GameObjects.Sprite;
  private readonly legFar: Phaser.GameObjects.Sprite;
  private readonly tail?: Phaser.GameObjects.Sprite;

  private readonly geo: RigGeometry;
  private readonly torsoH: number;
  private readonly rigScale: number;
  private readonly displayHeight: number;
  private standFootY = 0;
  private phase = 0;
  private speedNorm = 0;
  private pose: Pose = 'idle';
  private facing = 1;

  constructor(scene: Phaser.Scene, x: number, y: number, parts: RigParts, displayHeight: number, geometry?: Partial<RigGeometry>) {
    this.displayHeight = displayHeight;
    this.geo = {
      ...DEFAULT_GEOMETRY,
      ...geometry,
      origin: { ...DEFAULT_GEOMETRY.origin, ...geometry?.origin },
      rest: { ...DEFAULT_GEOMETRY.rest, ...geometry?.rest },
      joints: { ...DEFAULT_GEOMETRY.joints, ...geometry?.joints },
    };

    const tex = scene.textures;
    this.torsoH = tex.get(parts.torso).getSourceImage().height;
    const headH = tex.get(parts.head).getSourceImage().height;
    const legH = tex.get(parts.leg).getSourceImage().height;
    const naturalH = legH * 0.94 + this.torsoH * 0.82 + headH * 0.92;
    this.rigScale = displayHeight / naturalH;
    // Distance from the hip (body origin) down to a straight leg's foot. The body is
    // shifted up by this so the container's y is the FEET — i.e. scenes place the rig on
    // the ground line and it stands ON it (fixes characters being cut off below).
    this.standFootY = (0.95 * legH + 0.04 * this.torsoH) * this.rigScale;

    const mk = (key: string, origin: readonly [number, number], far = false) => {
      const s = scene.add.sprite(0, 0, key).setOrigin(origin[0], origin[1]).setScale(this.rigScale);
      if (far) s.setTint(FAR_TINT);
      return s;
    };
    this.tail = parts.tail ? mk(parts.tail, this.geo.origin.tail) : undefined;
    this.legFar = mk(parts.leg, this.geo.origin.leg, true);
    this.armFar = mk(parts.arm, this.geo.origin.arm, true);
    this.torso = mk(parts.torso, this.geo.origin.torso);
    this.legNear = mk(parts.leg, this.geo.origin.leg);
    this.head = mk(parts.head, this.geo.origin.head);
    this.armNear = mk(parts.arm, this.geo.origin.arm);

    const layers = [this.legFar, this.armFar, this.torso, this.legNear, this.head, this.armNear];
    this.body = scene.add.container(0, 0, this.tail ? [this.tail, ...layers] : layers);
    this.container = scene.add.container(x, y, [this.body]);
    this.apply(0, 0);
  }

  setMotion(speedNorm: number): void {
    this.speedNorm = Phaser.Math.Clamp(speedNorm, 0, 1);
    if (this.pose === 'idle' && this.speedNorm > 0.02) this.pose = 'run';
    else if (this.pose === 'run' && this.speedNorm <= 0.02) this.pose = 'idle';
  }

  setPose(pose: Pose): void {
    this.pose = pose;
  }

  setFacing(facing: number): void {
    this.facing = facing < 0 ? -1 : 1;
    this.container.scaleX = this.facing;
  }

  update(dt: number): void {
    switch (this.pose) {
      case 'run': {
        const freqHz = 1.0 + this.speedNorm * 1.8;
        this.phase += dt * freqHz * Math.PI * 2;
        this.apply(this.phase, this.speedNorm);
        break;
      }
      case 'celebrate':
        this.phase += dt * 6;
        this.applyCelebrate(this.phase);
        break;
      case 'exhausted':
        this.phase += dt * 2.4;
        this.applyExhausted(this.phase);
        break;
      default:
        this.phase += dt * 1.8;
        this.applyIdle(this.phase);
    }
  }

  /** Joint world position relative to the hip (rotated by lean, scaled). */
  private joint(off: [number, number], lean: number): [number, number] {
    const x = off[0] * this.rigScale;
    const y = off[1] * this.torsoH * this.rigScale;
    return [x * Math.cos(lean) - y * Math.sin(lean), x * Math.sin(lean) + y * Math.cos(lean)];
  }

  private apply(phase: number, speed: number): void {
    const g = this.geo;
    const lean = -0.12 * speed + g.rest.torso;
    const bob = (0.5 + 0.5 * Math.sin(phase * 2)) * this.displayHeight * 0.06 * speed;
    this.body.y = -this.standFootY - bob;

    const neck = this.joint(g.joints.neck, lean);
    const shoulder = this.joint(g.joints.shoulder, lean);
    const hip = this.joint(g.joints.hip, lean);
    const tailBase = this.joint(g.joints.tail, lean);

    const legSwing = g.legSwing * speed;
    const armSwing = g.armSwing * speed;
    const sinP = Math.sin(phase);
    const sinPi = Math.sin(phase + Math.PI);

    this.place(this.torso, 0, 0, lean);
    this.place(this.head, neck[0], neck[1], g.rest.head + lean * 0.5);
    this.place(this.legNear, hip[0] + 4, hip[1], g.rest.leg + sinP * legSwing + lean);
    this.place(this.legFar, hip[0] - 4, hip[1], g.rest.leg + sinPi * legSwing + lean);
    this.place(this.armNear, shoulder[0] + 6, shoulder[1], g.rest.arm - sinP * armSwing + lean);
    this.place(this.armFar, shoulder[0] + 2, shoulder[1], g.rest.arm - sinPi * armSwing + lean);
    if (this.tail) this.place(this.tail, tailBase[0], tailBase[1], g.rest.tail + Math.sin(phase - 0.6) * 0.3 * speed + lean);
  }

  private applyIdle(phase: number): void {
    this.apply(0, 0);
    this.torso.scaleY = this.rigScale * (1 + Math.sin(phase) * 0.02);
    this.head.rotation = this.geo.rest.head + Math.sin(phase) * 0.03;
    if (this.tail) this.tail.rotation = this.geo.rest.tail + Math.sin(phase * 0.8) * 0.12;
  }

  private applyCelebrate(phase: number): void {
    const g = this.geo;
    const neck = this.joint(g.joints.neck, 0);
    const shoulder = this.joint(g.joints.shoulder, 0);
    const hip = this.joint(g.joints.hip, 0);
    const tailBase = this.joint(g.joints.tail, 0);
    this.body.y = -this.standFootY - Math.abs(Math.sin(phase)) * this.displayHeight * 0.12;
    this.place(this.torso, 0, 0, 0);
    this.place(this.head, neck[0], neck[1], Math.sin(phase * 2) * 0.08);
    this.place(this.legNear, hip[0] + 4, hip[1], 0.25);
    this.place(this.legFar, hip[0] - 4, hip[1], -0.25);
    this.place(this.armNear, shoulder[0] + 6, shoulder[1], g.rest.arm + 1.5 + Math.sin(phase * 2) * 0.2);
    this.place(this.armFar, shoulder[0] + 2, shoulder[1], g.rest.arm + 1.7 + Math.sin(phase * 2) * 0.2);
    if (this.tail) this.place(this.tail, tailBase[0], tailBase[1], g.rest.tail + Math.sin(phase * 2) * 0.4);
  }

  private applyExhausted(phase: number): void {
    const g = this.geo;
    const lean = 0.16;
    const pant = Math.sin(phase) * 0.03;
    const neck = this.joint(g.joints.neck, lean);
    const shoulder = this.joint(g.joints.shoulder, lean);
    const hip = this.joint(g.joints.hip, lean);
    const tailBase = this.joint(g.joints.tail, lean);
    this.body.y = -this.standFootY + this.displayHeight * 0.02;
    this.place(this.torso, 0, 0, lean);
    this.torso.scaleY = this.rigScale * (1 + pant);
    this.place(this.head, neck[0], neck[1], 0.3 + lean);
    this.place(this.legNear, hip[0] + 8, hip[1], 0.3 + lean);
    this.place(this.legFar, hip[0] - 8, hip[1], -0.2 + lean);
    this.place(this.armNear, shoulder[0], shoulder[1], g.rest.arm + 0.25 + lean);
    this.place(this.armFar, shoulder[0], shoulder[1], g.rest.arm + 0.35 + lean);
    if (this.tail) this.place(this.tail, tailBase[0], tailBase[1], g.rest.tail + 0.4 + lean);
  }

  private place(sprite: Phaser.GameObjects.Sprite, x: number, y: number, rotation: number): void {
    sprite.setPosition(x, y);
    sprite.rotation = rotation;
    sprite.scaleX = this.rigScale;
    sprite.scaleY = this.rigScale;
  }

  destroy(): void {
    this.container.destroy();
  }
}
